import type { Invoice } from '@/types/invoice';

function finiteAmount(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function finiteAmountOrUndefined(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Invoice totals are frozen financial facts from the backend. Recalculating
 * them from display line items loses tax, credits, deposits, and masked data.
 */
export function getInvoiceFinancialSummary(
  invoice: Pick<
    Invoice,
    | 'subtotal'
    | 'discountTotal'
    | 'membershipDiscountTotal'
    | 'taxTotal'
    | 'total'
    | 'amountPaid'
    | 'balanceDue'
    | 'financialFieldPresence'
  > & { tipAmount?: number },
) {
  const total = finiteAmount(invoice.total);
  const amountPaid = finiteAmount(invoice.amountPaid);
  const hasRawBalanceDue = invoice.financialFieldPresence?.balanceDue ?? invoice.balanceDue != null;
  const rawBalanceDue = hasRawBalanceDue ? finiteAmountOrUndefined(invoice.balanceDue) : undefined;
  const canDeriveBalanceDue = invoice.financialFieldPresence
    ? invoice.financialFieldPresence.total && invoice.financialFieldPresence.amountPaid
    : finiteAmountOrUndefined(invoice.total) !== undefined &&
      finiteAmountOrUndefined(invoice.amountPaid) !== undefined;

  return {
    subtotal: finiteAmount(invoice.subtotal),
    discountTotal: finiteAmount(invoice.discountTotal),
    membershipDiscountTotal: finiteAmount(invoice.membershipDiscountTotal),
    taxTotal: finiteAmount(invoice.taxTotal),
    // NOT-535 — surfaced so the breakdown can show the gratuity on its own
    // line. `total` deliberately EXCLUDES it: a tip is paid on top of the bill,
    // not billed. Render it below the totals, never between Tax and Total —
    // there it reads as part of the sum and the document stops adding up.
    tipAmount: finiteAmount(invoice.tipAmount),
    total,
    amountPaid,
    balanceDue:
      rawBalanceDue ?? (canDeriveBalanceDue ? Math.max(0, total - amountPaid) : undefined),
  };
}
